import { useState, useEffect, useRef } from 'react';
import { EbookTemplate } from '../../../mocks/ebookTemplates';

interface Props {
  template: EbookTemplate;
  onClose: () => void;
}

function generateShareUrl(template: EbookTemplate): string {
  return `https://openclaw.ai/templates/${template.id}`;
}

export default function ShareTemplateModal({ template, onClose }: Props) {
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'link' | 'embed' | 'qr'>('link');
  const backdropRef = useRef<HTMLDivElement>(null);

  const shareUrl = generateShareUrl(template);
  const embedCode = `<iframe src="${shareUrl}/embed" width="400" height="560" frameborder="0" allowfullscreen title="${template.name}"></iframe>`;

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const copyToClipboard = async (text: string, setter: (v: boolean) => void) => {
    try {
      await navigator.clipboard.writeText(text);
      setter(true);
      setTimeout(() => setter(false), 2200);
    } catch {
      // fallback
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setter(true);
      setTimeout(() => setter(false), 2200);
    }
  };

  const shareOnTwitter = () => {
    const text = encodeURIComponent(`Check out this amazing eBook template: "${template.name}" — created with @OpenClawAI`);
    const url = encodeURIComponent(shareUrl);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank', 'width=580,height=440');
  };

  const shareOnLinkedIn = () => {
    const url = encodeURIComponent(shareUrl);
    const title = encodeURIComponent(template.name);
    const summary = encodeURIComponent(template.description);
    window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${title}&summary=${summary}`, '_blank', 'width=580,height=520');
  };

  const shareOnFacebook = () => {
    const url = encodeURIComponent(shareUrl);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank', 'width=580,height=440');
  };

  const shareOnWhatsApp = () => {
    const text = encodeURIComponent(`Check out this eBook template: "${template.name}" ${shareUrl}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const nativeShare = () => {
    if (navigator.share) {
      navigator.share({
        title: template.name,
        text: template.description,
        url: shareUrl,
      }).catch(() => {});
    }
  };

  const BADGE_STYLES: Record<string, string> = {
    Popular: 'bg-amber-100 text-amber-700',
    New: 'bg-emerald-100 text-emerald-700',
    Trending: 'bg-rose-100 text-rose-700',
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div
        ref={backdropRef}
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-extrabold text-slate-900">Share Template</h3>
            <p className="text-xs text-slate-400 mt-0.5">Share this template with your audience</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-xl border border-slate-200 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-all cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-base"></i>
          </button>
        </div>

        {/* Template Mini Card */}
        <div className="mx-6 mb-5 rounded-2xl border-2 border-slate-100 overflow-hidden flex items-stretch bg-slate-50">
          <div className="w-16 flex-shrink-0 relative overflow-hidden">
            <img
              src={template.image}
              alt={template.name}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-slate-50/30" />
            <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: template.color }}></div>
          </div>
          <div className="flex-1 px-4 py-3">
            <div className="flex items-center gap-2 mb-0.5">
              <p className="text-sm font-extrabold text-slate-900 truncate">{template.name}</p>
              {template.badge && (
                <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-bold ${BADGE_STYLES[template.badge] || ''}`}>
                  {template.badge}
                </span>
              )}
            </div>
            <p className="text-xs text-slate-400 truncate mb-2">{template.category}</p>
            <div className="flex items-center gap-3 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-amber-400 text-xs"></i>
                {template.rating}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-book-open-line text-slate-400 text-xs"></i>
                {template.chapters} ch.
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-download-line text-slate-400 text-xs"></i>
                {(template.downloads / 1000).toFixed(1)}k
              </span>
            </div>
          </div>
        </div>

        {/* Quick Social Buttons */}
        <div className="px-6 mb-5">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Share via</p>
          <div className="grid grid-cols-4 gap-2.5">
            {/* Twitter/X */}
            <button
              onClick={shareOnTwitter}
              className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-black hover:bg-slate-800 transition-colors cursor-pointer group"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-twitter-x-line text-white text-base"></i>
              </div>
              <span className="text-xs text-white font-semibold">X</span>
            </button>

            {/* LinkedIn */}
            <button
              onClick={shareOnLinkedIn}
              className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-[#0A66C2] hover:bg-[#0958a8] transition-colors cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-linkedin-fill text-white text-base"></i>
              </div>
              <span className="text-xs text-white font-semibold">LinkedIn</span>
            </button>

            {/* Facebook */}
            <button
              onClick={shareOnFacebook}
              className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-[#1877F2] hover:bg-[#1464d4] transition-colors cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-facebook-fill text-white text-base"></i>
              </div>
              <span className="text-xs text-white font-semibold">Facebook</span>
            </button>

            {/* WhatsApp */}
            <button
              onClick={shareOnWhatsApp}
              className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-[#25D366] hover:bg-[#1eb854] transition-colors cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-whatsapp-line text-white text-base"></i>
              </div>
              <span className="text-xs text-white font-semibold">WhatsApp</span>
            </button>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="px-6 mb-4">
          <div className="flex items-center p-1 bg-slate-100 rounded-xl gap-0.5">
            {(['link', 'embed', 'qr'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-1.5 px-3 rounded-lg text-xs font-semibold transition-all cursor-pointer capitalize whitespace-nowrap ${
                  activeTab === tab ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {tab === 'link' ? 'Copy Link' : tab === 'embed' ? 'Embed Code' : 'QR Code'}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="px-6 pb-6">
          {activeTab === 'link' && (
            <div>
              <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl border border-slate-200">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  <i className="ri-link-m text-slate-400 text-sm"></i>
                </div>
                <input
                  readOnly
                  value={shareUrl}
                  className="flex-1 text-xs text-slate-600 bg-transparent outline-none truncate font-mono"
                />
                <button
                  onClick={() => copyToClipboard(shareUrl, setLinkCopied)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${
                    linkCopied
                      ? 'bg-emerald-500 text-white'
                      : 'bg-slate-800 text-white hover:bg-slate-900'
                  }`}
                >
                  <i className={`${linkCopied ? 'ri-check-line' : 'ri-file-copy-line'} text-xs`}></i>
                  {linkCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              {linkCopied && (
                <div className="flex items-center gap-2 mt-2.5 text-xs text-emerald-600 font-medium">
                  <i className="ri-checkbox-circle-fill"></i>
                  Link copied to clipboard!
                </div>
              )}
            </div>
          )}

          {activeTab === 'embed' && (
            <div>
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 mb-3">
                <code className="text-xs text-slate-600 font-mono break-all leading-relaxed block">
                  {embedCode}
                </code>
              </div>
              <button
                onClick={() => copyToClipboard(embedCode, setEmbedCopied)}
                className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                  embedCopied ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-white hover:bg-slate-900'
                }`}
              >
                <i className={`${embedCopied ? 'ri-check-line' : 'ri-code-s-slash-line'} text-sm`}></i>
                {embedCopied ? 'Copied!' : 'Copy Embed Code'}
              </button>
            </div>
          )}

          {activeTab === 'qr' && (
            <div className="text-center">
              {/* QR code visual using CSS grid pattern */}
              <div className="flex justify-center mb-3">
                <div className="p-4 bg-white border-2 border-slate-200 rounded-2xl inline-block">
                  <div
                    className="w-32 h-32 relative overflow-hidden rounded-lg"
                    style={{ background: `linear-gradient(135deg, ${template.color}22 0%, ${template.color}08 100%)` }}
                  >
                    {/* Simulated QR pattern */}
                    <div className="absolute inset-0 grid grid-cols-8 gap-0.5 p-1.5">
                      {Array.from({ length: 64 }).map((_, i) => {
                        const filled = (i * 7 + 13) % 3 !== 0 || i % 11 === 0 || (i > 20 && i < 44 && i % 5 === 0);
                        return (
                          <div
                            key={i}
                            className="rounded-sm"
                            style={{ backgroundColor: filled ? template.color : 'transparent', opacity: filled ? 0.85 : 0 }}
                          />
                        );
                      })}
                    </div>
                    {/* Center logo */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div
                        className="w-9 h-9 rounded-xl flex items-center justify-center shadow-lg"
                        style={{ backgroundColor: template.color }}
                      >
                        <i className="ri-book-2-line text-white text-base"></i>
                      </div>
                    </div>
                    {/* Corner marks */}
                    {[[0,0],[0,1],[1,0]].map(([r,c], i) => (
                      <div
                        key={i}
                        className="absolute w-8 h-8 border-4 rounded-sm"
                        style={{
                          borderColor: template.color,
                          top: r ? 'auto' : '6px',
                          bottom: r ? '6px' : 'auto',
                          left: c ? 'auto' : '6px',
                          right: c ? '6px' : 'auto',
                        }}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-xs text-slate-500 mb-3">Scan to open this template</p>
              <button
                onClick={() => copyToClipboard(shareUrl, setCopied)}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                  copied ? 'bg-emerald-500 text-white' : 'bg-slate-800 text-white hover:bg-slate-900'
                }`}
              >
                <i className={`${copied ? 'ri-check-line' : 'ri-download-2-line'} text-sm`}></i>
                {copied ? 'Link Copied!' : 'Copy Link Instead'}
              </button>
            </div>
          )}
        </div>

        {/* Native share button (mobile) */}
        {typeof navigator !== 'undefined' && 'share' in navigator && (
          <div className="px-6 pb-5 -mt-2">
            <button
              onClick={nativeShare}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold border-2 border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-share-line text-base"></i>
              More Share Options
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
